import React,{useState} from 'react';
import {Camera,ScanLine,FileImage,ClipboardCopy} from 'lucide-react';
import NfeKeyScanner from './NfeKeyScanner.jsx';
import DanfeReview from './DanfeReview.jsx';
import {extractNfeAccessKey,readNfeKeyFromImage} from './nfeKeyCapture.js';
import {matchNfeItems,parseNfeXml,prepareNfeReceipt,validCnpj} from './nfeIntake.js';

const fileLimit=3*1024*1024;
const money=value=>new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(value||0);
const statusLabels={ready:'Identificado',unmatched:'Não localizado',ambiguous:'Código duplicado',inactive:'Produto inativo/sem controle',unit_mismatch:'Unidade divergente',fractional:'Quantidade fracionária não permitida'};
const onlyKey=value=>String(value||'').trim().toUpperCase();
export default function NfeReceiving({organization,products=[],canInventory=false,catalogReady=false,onPost,onReload,onConfigureCnpj,onAnalyzeDanfe,recipientCnpj=''}) {
  const [note,setNote]=useState(null),[rows,setRows]=useState([]),[hash,setHash]=useState('');
  const [message,setMessage]=useState(''),[busy,setBusy]=useState(false),[approved,setApproved]=useState(false);
  const [cnpjDraft,setCnpjDraft]=useState(''),[configBusy,setConfigBusy]=useState(false);
  const [keyInput,setKeyInput]=useState(''),[scannedKey,setScannedKey]=useState('');
  const [scannerOpen,setScannerOpen]=useState(false),[photoBusy,setPhotoBusy]=useState(false);
  const [keyError,setKeyError]=useState('');
  function acceptKey(raw){
    try{
      const key=extractNfeAccessKey(raw);
      setScannedKey(key);setKeyInput(key);setKeyError('');
    }catch(e){setKeyError(e.message||'Chave de acesso inválida.')}
  }
  async function readPhoto(file){
    if(!file)return;
    setPhotoBusy(true);setKeyError('');
    try{
      const key=await readNfeKeyFromImage(file);
      acceptKey(key);
    }catch(e){setKeyError(e.message||'Não foi possível reconhecer o código da nota.')}
    finally{setPhotoBusy(false)}
  }
  const canPost=import.meta.env.VITE_SMARTPDV_NFE_RECEIPT_POST_ENABLED==='true';
  const pending=rows.filter(item=>item.status!=='ready');
  async function readFile(file){
    setNote(null);setRows([]);setHash('');setApproved(false);setMessage('');
    if(!file)return;
    if(!catalogReady)return setMessage('Aguarde o carregamento completo do catálogo desta empresa.');
    if(file.size>fileLimit||!file.name.toLowerCase().endsWith('.xml'))return setMessage('Selecione um XML até 3 MB.');
    setBusy(true);
    try{
      const buffer=await file.arrayBuffer();
      const xml=new TextDecoder('utf-8',{fatal:true}).decode(buffer);
      const parsed=parseNfeXml(xml);
      const digest=await crypto.subtle.digest('SHA-256',buffer);
      const fingerprint=Array.from(new Uint8Array(digest),n=>n.toString(16).padStart(2,'0')).join('');
      setNote(parsed);setRows(matchNfeItems(parsed.lines,products));setHash(fingerprint);
    }catch(e){setMessage(e.message||'Arquivo inválido.')}finally{setBusy(false)}
  }
  function linkProduct(index,code){
    const candidate=products.filter(p=>p.active!==false&&p.track_inventory&&
      (onlyKey(p.sku)===onlyKey(code)||onlyKey(p.barcode)===onlyKey(code)));
    setRows(current=>current.map((item,i)=>{
      if(i!==index)return item;
      if(candidate.length!==1)return {...item,status:'unmatched',productId:''};
      const product=candidate[0];
      if(onlyKey(product.unit)!==onlyKey(item.unit))return {...item,status:'unit_mismatch',productId:''};
      if(!product.allow_fractional&&item.quantity!==Math.trunc(item.quantity))
        return {...item,status:'fractional',productId:''};
      return {...item,status:'ready',productId:product.id};
    }));
    setApproved(false);
  }
  async function post(){
    if(!approved||!canInventory||!canPost||!onPost||pending.length||busy)return;
    if(scannedKey&&note?.key!==scannedKey){
      setMessage('O XML não corresponde à chave da foto/câmera. Nenhuma entrada foi lançada.');return;
    }
    setBusy(true);setMessage('');
    try{
      const payload=prepareNfeReceipt(note,rows,hash);
      payload.operator_declared_physical_receipt=true;
      const result=await onPost(payload);
      setMessage(result?.idempotent_replay?'NF-e já recebida anteriormente; estoque não foi duplicado.':
        'Entrada de mercadorias registrada com histórico auditável.');
      setApproved(false);
      await onReload?.();
    }catch(e){setMessage('Entrada NÃO lançada: '+(e.message||'verifique as permissões e a NF-e.'))}
    finally{setBusy(false)}
  }
  const statusReady=Boolean(note&&hash&&rows.length&&!pending.length&&recipientCnpj===note.recipientCnpj&&canInventory&&(!scannedKey||scannedKey===note.key));
  return <section className="panel nfeReceiving" aria-label="Receber mercadorias de NF-e">
    <h2>Entrada de mercadoria por XML da NF-e</h2>
    <p className="nfePreviewWarning" role="status"><strong>Prévia para conferência.</strong> Nenhum estoque será alterado nesta versão.</p>
    <p>Leia os itens do arquivo do fornecedor e confira com o catálogo da empresa ativa. A importação não altera preços de venda.</p>
    <p><strong>Empresa:</strong> {organization?.name||'—'} · <strong>Modo:</strong> NF-e modelo 55 · prévia local</p>
    <div className="nfeCameraSection">
      <h3>Ler nota pela câmera</h3>
      <p>Escaneie o código de barras do DANFE, fotografe o código ou digite os 44 dígitos. O KAIZEN identificará a chave da nota, mas só poderá importar os produtos quando o XML completo estiver disponível.</p>
      <div className="nfeCameraActions">
        <button type="button" onClick={()=>setScannerOpen(true)}
          disabled={busy||photoBusy}><ScanLine size={18}/> Abrir câmera</button>
        <label className="nfePhotoButton">
          <FileImage size={18}/> {photoBusy?'Lendo imagem…':'Fotografar código / escolher imagem'}
          <input type="file" accept="image/png,image/jpeg,image/webp" capture="environment"
            disabled={busy||photoBusy} onChange={e=>{
              readPhoto(e.target.files?.[0]);e.target.value='';
            }}/>
        </label>
      </div>
      <div className="nfeKeyManual">
        <label>Chave de acesso da NF-e (44 dígitos)
          <input inputMode="numeric" autoComplete="off" aria-label="Chave de acesso da NF-e"
            value={keyInput} maxLength={66} placeholder="Digite ou escaneie a chave do DANFE"
            onChange={e=>{setKeyInput(e.target.value);setKeyError('')}}/>
        </label>
        <button type="button" onClick={()=>acceptKey(keyInput)}>Validar chave</button>
      </div>
      {scannedKey&&<div className="nfeKeyFound" role="status">
        <strong>Chave identificada:</strong> <code>{scannedKey}</code>
        <button type="button" onClick={async()=>{
          try{await navigator.clipboard.writeText(scannedKey)}
          catch{setKeyError('Copie a chave exibida para usar na consulta oficial.')}
        }}><ClipboardCopy size={16}/> Copiar</button>
        {note&&note.key!==scannedKey&&<p role="alert">O arquivo XML aberto não corresponde à chave capturada.</p>}
      </div>}
      {keyError&&<p className="nfeKeyError" role="alert">{keyError}</p>}
      <p className="nfeSefazHelp"><strong>Recuperação do XML:</strong> a consulta pública não fornece automaticamente o XML. Consulte a <a target="_blank" rel="noopener noreferrer" href="https://www.sef.sc.gov.br/servicos/consultar-nf-e-por-chave-de-acesso">SEFAZ/SC</a> ou solicite o XML ao fornecedor. A busca automática exige integração fiscal autorizada e certificado digital da empresa; ainda não está conectada.</p>
    </div>
    {scannerOpen&&<NfeKeyScanner onDetected={acceptKey} onClose={()=>setScannerOpen(false)}/>}
    <DanfeReview organization={organization} products={products} catalogReady={catalogReady}
      canInventory={canInventory} scannedKey={scannedKey} recipientCnpj={recipientCnpj}
      onAnalyze={onAnalyzeDanfe}/>
    <div className="nfeXmlInput">
    <label className="nfeFilePicker">XML completo da NF-e autorizada (.xml, até 3 MB)
      <input type="file" accept=".xml,text/xml,application/xml" disabled={busy||!catalogReady}
        onChange={e=>{readFile(e.target.files?.[0]);e.target.value='';}}/>
    </label>
    </div>
    {!catalogReady&&<p className="nfeCatalogStatus" role="status">Carregando catálogo completo para reconhecer os itens, sem risco de comparar uma lista parcial.</p>}
    <small>O arquivo é lido no navegador. A indicação de autorização no XML não substitui validação criptográfica da assinatura nem consulta atualizada à SEFAZ.</small>
    {recipientCnpj?<p><strong>CNPJ autorizado para conferência:</strong> {recipientCnpj}</p>:
      <p role="alert">CNPJ destinatário desta empresa não configurado. O gestor deve autorizar o CNPJ antes de registrar estoque.</p>}
    {typeof onConfigureCnpj==='function'&&<div>
      <label>CNPJ destinatário da empresa (configuração administrativa)
        <input value={cnpjDraft} inputMode="numeric" placeholder="14 dígitos"
          onChange={e=>setCnpjDraft(e.target.value.replace(/\D/g,'').slice(0,14))}/>
      </label>
      <button type="button" disabled={configBusy||!validCnpj(cnpjDraft)} onClick={async()=>{
        setConfigBusy(true);setMessage('');
        try{await onConfigureCnpj(cnpjDraft);setMessage('CNPJ registrado para esta empresa. Confirme seus dados antes de importar.')}
        catch(e){setMessage('CNPJ não salvo: '+(e.message||'erro de permissão'))}
        finally{setConfigBusy(false)}
      }}>Configurar CNPJ da empresa</button>
    </div>}
    {note&&<div>
      <h3>Conferência da NF-e</h3>
      <p><strong>Chave:</strong> <code>{note.key}</code></p>
      <p><strong>Fornecedor:</strong> {note.supplierName} — {note.supplierCnpj}</p>
      <p><strong>Destinatário:</strong> {note.recipientCnpj} · <strong>Itens:</strong> {rows.length} · <strong>Pendentes:</strong> {pending.length}</p>
      {scannedKey&&scannedKey!==note.key&&
        <p role="alert">A chave escaneada não coincide com o XML selecionado; o registro de estoque é bloqueado.</p>}
      {recipientCnpj&&recipientCnpj!==note.recipientCnpj&&
        <p role="alert">O CNPJ destinatário do XML é diferente do CNPJ autorizado da empresa. Não registre.</p>}
      <div className="responsiveTable nfeItemsTable" style={{overflowX:'auto'}}>
        <table>
          <thead><tr><th>Item do XML</th><th>GTIN</th><th>Qtde</th><th>Unid.</th><th>Custo unitário</th><th>Catálogo</th></tr></thead>
          <tbody>{rows.map((row,i)=><tr key={row.line}>
            <td>{row.name}<small> · #{row.line} · {row.supplierCode}</small></td>
            <td>{row.barcode||'Sem GTIN'}</td><td>{row.quantity}</td><td>{row.unit}</td><td>{money(row.unitPrice)}</td>
            <td>{row.status==='ready'?<>
                <strong>{products.find(p=>p.id===row.productId)?.name||'Identificado'}</strong>
                <small> · {statusLabels[row.status]}</small>
              </>:<div>
                <small>{statusLabels[row.status]||'Revisão necessária'}</small>
                <input aria-label={'Buscar SKU ou código do item '+row.line} placeholder="SKU/GTIN exato do produto" type="search"
                  onBlur={e=>linkProduct(i,e.target.value)}
                  onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();linkProduct(i,e.currentTarget.value)}}}/>
              </div>}</td>
          </tr>)}</tbody>
        </table>
      </div>
      <label className="nfeConfirm"><input type="checkbox" checked={approved} onChange={e=>setApproved(e.target.checked)}/>
        Conferi fisicamente as quantidades, unidades, produtos e o CNPJ desta nota. Estou autorizando apenas o recebimento de estoque, não uma validação fiscal.
      </label>
      <button type="button" className="nfeSubmit" disabled={!approved||!statusReady||!canPost||busy||!onPost}
        onClick={post}>{busy?'Verificando…':'Registrar entrada única no estoque'}</button>
      {!canPost&&<p role="status">Gravação bloqueada neste deploy até a migração, os testes de segurança e a habilitação explícita da função. A conferência do XML já pode ser testada.</p>}
    </div>}
    {message&&<p role="status">{message}</p>}
  </section>;
}
